import type { SessionCredentials } from 'ts-components/PiChat/protocol';
import type { GetCredentials } from 'ts-components/PiChat/usePiChat';
import type { TemporaryApiKey } from 'services/TemporaryApiKeyService';

export const CREDENTIALS_REUSE_MIN_REMAINING_MS = 5 * 60 * 1000;

export interface TemporaryCredentialsOptions {
  fetchKey: () => Promise<TemporaryApiKey>;
  now?: () => number;
}

export interface TemporaryCredentials {
  get: GetCredentials;
}

const toCredentials = (key: TemporaryApiKey): SessionCredentials => ({
  apiKey: key.apiKey,
  userId: key.userId,
  businessId: key.businessId,
  expires: key.expires,
});

export const createTemporaryCredentials = ({
  fetchKey,
  now = () => Date.now(),
}: TemporaryCredentialsOptions): TemporaryCredentials => {
  let latest: {
    credentials: SessionCredentials;
    expiresAt: number;
  } | null = null;
  let inFlight: Promise<SessionCredentials> | null = null;

  const isFresh = (expiresAt: number) =>
    expiresAt - now() > CREDENTIALS_REUSE_MIN_REMAINING_MS;

  const fetchFresh = (): Promise<SessionCredentials> => {
    if (!inFlight) {
      inFlight = fetchKey()
        .then(
          (key) => {
            const credentials = toCredentials(key);
            latest = { credentials, expiresAt: Date.parse(key.expires) };
            return credentials;
          },
          (error: unknown) => {
            latest = null;
            throw error;
          }
        )
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  };

  return {
    get: (purpose) => {
      if (purpose === 'user.message' && latest && isFresh(latest.expiresAt)) {
        return Promise.resolve(latest.credentials);
      }
      return fetchFresh();
    },
  };
};
